/**
 * DomainPackSelector (DC-1518, v3.0 Block 3).
 *
 * The honest v3.0 shape: binding is consultant-set in this phase, so this
 * component DISPLAYS pack resolution — it does not choose packs when a
 * binding exists.
 *
 * - Domain-bound: a static identity row, not a control. The perspective
 *   control (passed as children) is HIDDEN, not disabled — a disabled control
 *   implies you could enable it.
 * - Unbound / legacy: the children render unchanged, plus one muted caption
 *   naming the fallback pack — the only UI change legacy users see.
 *
 * No terracotta anywhere here: it's plumbing identity, not a decision point.
 *
 * UX spec: docs/design/v3.0-domain-pack-surfaces-ux-spec.md §4.
 */

import React from 'react';
import { Info, ShieldCheck } from 'lucide-react';
import type { PackResolution } from './packJudgmentTypes';

const BOUND_TOOLTIP =
  "This domain's pack governs Contextual Validity here. Bindings are managed with your Compass consultant.";

export interface DomainPackSelectorProps {
  /** Null while resolution is loading/unavailable — children render unchanged. */
  resolution: PackResolution | null;
  /** The existing perspective control (unchanged for unbound/legacy flows). */
  children?: React.ReactNode;
}

export function DomainPackSelector({ resolution, children }: DomainPackSelectorProps): React.ReactElement {
  if (resolution?.bound) {
    return (
      <div className="dp-selector-bound" data-testid="dp-selector-bound">
        <span className="dp-selector-icon">
          <ShieldCheck size={16} aria-hidden="true" />
        </span>
        <span className="dp-selector-ref">{resolution.pack_ref}</span>
        {resolution.domain_name && (
          <span className="dp-selector-domain">
            bound to domain &ldquo;{resolution.domain_name}&rdquo;
          </span>
        )}
        <button type="button" className="dp-selector-info" title={BOUND_TOOLTIP} aria-label={BOUND_TOOLTIP}>
          <Info size={14} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <>
      {children}
      {resolution && (
        <p className="dp-selector-caption" data-testid="dp-selector-caption">
          assessed with {resolution.pack_ref}
        </p>
      )}
    </>
  );
}

export default DomainPackSelector;
